import React, { HTMLAttributes, useMemo, JSX } from 'react';
import {
  AlertDataProvider,
  AlertMessageType,
  AlertPositionType,
  AlertType,
  useAlertsContext
} from './ContextAndReducer';
import Keys from '../../../../_Translations/generated/alert.en.t.json.keys';
import fallback from '../../../../_Translations/generated/alert.en.t.json';
import useTranslation from '../../../Hooks/useTranslation';

import { Alert as AlertReactStrap } from 'reactstrap';
import { createUniqueId } from '@ibe/services';
import { useMount, useUnmount } from 'react-use';
export interface AlertProps {
  children: JSX.Element;
  customComponent?: (alert: AlertMessageType) => JSX.Element;
  alertContainerProps?: HTMLAttributes<HTMLDivElement>;
  alertWrapperProps?: HTMLAttributes<HTMLDivElement>;
  /**
   * Should the Alert have a backdrop ?
   */
  backdropProps?: {
    hideAllOnClick?: boolean;
    props?: HTMLAttributes<HTMLDivElement>;
    onClick?: () => void;
  };
  /**
   * Should the Alert be fixed ?
   */
  fixed?: boolean;
}

const AlertWrapper = (props: AlertProps) => {
  return (
    <AlertDataProvider>
      <AlertContainer {...props} />
    </AlertDataProvider>
  );
};
type ClickEventType = React.MouseEvent<HTMLDivElement, MouseEvent>;
const AlertContainer = (props: AlertProps) => {
  const {
    children,
    customComponent,
    alertContainerProps,
    alertWrapperProps,
    backdropProps,
    fixed
  } = props;
  const { alerts, removeAllAlerts } = useAlertsContext();

  const keys: string[] = useMemo(() => {
    return alerts.map(alert => createUniqueId('alert', alert.id?.toString()));
  }, [alerts.length]);

  const onBackDropClick = () => {
    if (backdropProps?.onClick) backdropProps.onClick();
    if (backdropProps?.hideAllOnClick) removeAllAlerts();
  };

  const wrapperClicked = (e: ClickEventType) => {
    e.stopPropagation();
    onBackDropClick();
  };

  return (
    <div className="iso__alert__container" {...alertContainerProps}>
      {children}
      {backdropProps && alerts.length ? (
        <div
          onClick={onBackDropClick}
          className={`${backdropProps ? ' iso__alert__backdrop' : ''}${
            fixed ? ' iso__alert__backdrop--fixed' : ''
          }`}
          {...backdropProps?.props}
        ></div>
      ) : null}
      {alerts.map((alert, index) => {
        const { position = AlertPositionType.BOTTOM } = alert;
        return (
          <div
            className={`iso__alert__wrapper ${
              position === AlertPositionType.BOTTOM ? ' iso__alert__wrapper--bottom' : ''
            }${position === AlertPositionType.TOP ? '  iso__alert__wrapper--top' : ''}${
              position === AlertPositionType.CENTER ? ' iso__alert__wrapper--center' : ''
            }${fixed ? ' iso__alert__wrapper--fixed' : ''}`}
            key={keys[index]}
            {...alertWrapperProps}
            onClick={
              position === AlertPositionType.CENTER
                ? (e: ClickEventType) => wrapperClicked(e)
                : undefined
            }
          >
            <AlertBox alert={alert} component={customComponent} />
          </div>
        );
      })}
    </div>
  );
};

const AlertBox = (props: {
  component?: (alert: AlertMessageType) => JSX.Element;
  alert: AlertMessageType;
}) => {
  const { t } = useTranslation('nav-item-layout', fallback, Keys);

  const { removeAlert } = useAlertsContext();
  const { alert, component } = props;
  const { title, message, type = AlertType.WARNING, hideAfterTimeOut, id = 0 } = alert;

  let timeout: null | ReturnType<typeof setTimeout> = null;
  useMount(() => {
    if (hideAfterTimeOut) {
      timeout = setTimeout(() => {
        if (id) removeAlert(id);
      }, hideAfterTimeOut);
    }
  });

  useUnmount(() => {
    if (hideAfterTimeOut && timeout) clearTimeout(timeout);
  });

  if (component) return component(alert);

  const renderTitle = (): string => {
    return type === AlertType.DANGER
      ? t(Keys.ERROR)
      : type === AlertType.WARNING
      ? t(Keys.WARNING)
      : t(Keys.SUCCESS);
  };
  return (
    <AlertReactStrap className={`iso__alert__box`} color={'light'} toggle={() => removeAlert(id)}>
      <span
        className={`iso__alert__box__title ${
          type === AlertType.DANGER ? ' iso__alert__box__title--error' : ''
        }${type === AlertType.WARNING ? ' iso__alert__box__title--warning' : ''}${
          type === AlertType.SUCCESS ? ' iso__alert__box__title--success' : ''
        }`}
      >
        {title ? title : renderTitle()}
      </span>
      <p>{message}</p>
    </AlertReactStrap>
  );
};

export default AlertWrapper;
export { useAlertsContext, AlertType, AlertPositionType, useAlertsContext as useAlerts };
